import React from "react";
import { X, Clock, IndianRupee, Tag, PlayCircle } from "lucide-react";
import { getDiscountMeta } from "../../../utils/helpers";

const ViewServiceModal = ({ isOpen, onClose, service }) => {
  if (!isOpen || !service) return null;

 

  const isSinglePrice =
    typeof service.originalPrice === "number" &&
    typeof service.discountedPrice === "number";

  const isMultiPrice = Array.isArray(service.type) && service.type.length > 0;

  const discountType = service.discount?.type || null;

  const singleDiscountLabel = isSinglePrice
    ? getDiscountMeta(
        service.originalPrice,
        service.discountedPrice,
        discountType,
      )
    : null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl max-h-[90vh] shadow-2xl w-full max-w-2xl overflow-y-scroll no-scrollbar"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header / Image */}
        <div className="relative">
          {service.imageUrl ? (
            <img
              src={service.imageUrl}
              alt={service.name}
              className="w-full h-40 object-cover"
            />
          ) : (
            <div className="w-full h-20 bg-gray-200 flex items-center justify-center">
              <Tag size={48} className="text-gray-400" />
            </div>
          )}

          {/* Overlay Title */}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            <h2 className="text-xl font-bold text-white">{service.name}</h2>
            <p className="text-xs text-gray-200">
              {service.categoryId?.categoryName} •{" "}
              {service.subCategoryId?.subCategoryName}
            </p>
          </div>
          <div className="absolute top-3 left-3 flex gap-2">
            {singleDiscountLabel && (
              <span className="bg-green-600 text-white text-xs font-semibold px-2 py-1 rounded">
                {singleDiscountLabel}
              </span>
            )}

            <span className="bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
              ⭐ {service.ratings || 0}
            </span>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 bg-white/80 rounded-full p-1.5 hover:bg-white transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-6">
          {/* Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
              <Clock size={20} className="text-[#EDB5A4]" />
              <div>
                <p className="text-xs text-gray-500">Duration</p>
                <p className="text-sm font-semibold">{service.duration}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
              <Tag size={20} className="text-[#EDB5A4]" />
              <div>
                <p className="text-xs text-gray-500">Category</p>
                <p className="text-sm font-semibold capitalize">
                  {service.categoryId?.categoryName}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
              <Tag size={20} className="text-[#EDB5A4]" />
              <div>
                <p className="text-xs text-gray-500">Sub Category</p>
                <p className="text-sm font-semibold capitalize">
                  {service.subCategoryId?.subCategoryName}
                </p>
              </div>
            </div>
          </div>

          {isSinglePrice && (
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-3">
                Service Price
              </h3>

              <div className="flex justify-between items-center bg-gray-50 rounded-lg px-4 py-3 text-sm">
                <span className="text-gray-600">Price</span>

                <div className="flex items-center gap-2">
                  {service.discountedPrice < service.originalPrice && (
                    <span className="text-gray-400 line-through text-xs">
                      ₹{service.originalPrice}
                    </span>
                  )}

                  <span className="flex items-center gap-1 font-semibold text-gray-900">
                    <IndianRupee size={14} />
                    {service.discountedPrice}
                  </span>
                </div>
              </div>

              {/* Discount badge */}
              {singleDiscountLabel && (
                <div className="mt-2 text-xs font-semibold text-emerald-700">
                  {singleDiscountLabel}
                </div>
              )}
            </div>
          )}

          {isMultiPrice && (
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-3">
                Brand Pricing
              </h3>

              <div className="space-y-2">
                {service.type.map((item, idx) => {
                  const itemDiscountLabel = getDiscountMeta(
                    item.originalPrice,
                    item.discountedPrice,
                    discountType,
                  );

                  return (
                    <div
                      key={item._id || idx}
                      className="flex justify-between bg-gray-50 rounded-lg px-4 py-2 text-sm"
                    >
                      <div className="flex flex-col">
                        <span>{item.brand}</span>
                        <span>
                          {itemDiscountLabel && (
                            <div className="text-[0.67rem] font-medium text-emerald-700 mt-1">
                              {itemDiscountLabel}
                            </div>
                          )}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        {item.discountedPrice < item.originalPrice && (
                          <span className="text-gray-400 line-through text-xs">
                            ₹{item.originalPrice}
                          </span>
                        )}

                        <span className="flex items-center gap-1 font-semibold text-gray-900">
                          <IndianRupee size={14} />
                          {item.discountedPrice}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Extra Charges */}
          {service.extraCharges?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-3">
                Extra Charges
              </h3>
              <div className="space-y-2">
                {service.extraCharges.map((item) => (
                  <div
                    key={item._id}
                    className="flex justify-between bg-gray-50 rounded-lg px-4 py-2 text-sm"
                  >
                    <span>{item.title}</span>
                    <span className="flex items-center gap-1 font-semibold">
                      <IndianRupee size={14} />
                      {item.amount}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* DESCRIPTION */}
          {service.description?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-3">
                Service Details
              </h3>

              <div className="space-y-4">
                {service.description.map((section) => (
                  <div key={section._id}>
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">
                      {section.heading}
                    </h4>

                    <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                      {section.points.map((point, idx) => (
                        <li key={idx}>{point}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Demo Video */}
          {service.videoUrl ? (
            <div>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-3">
                <PlayCircle size={18} className="text-[#EDB5A4]" />
                Demo Video
              </h3>
              <div className="rounded-lg overflow-hidden shadow">
                <video
                  src={service.videoUrl}
                  controls
                  className="w-full h-[18rem] object-cover"
                />
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-gray-400 text-sm">
              <PlayCircle size={36} className="mx-auto mb-2" />
              No demo video available
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-primaryBtnColor text-white font-semibold rounded-xl hover:bg-primaryBtnHoverColor transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewServiceModal;

// import React from "react";
// import { X, Clock, IndianRupee, Tag, PlayCircle } from "lucide-react";

// const ViewServiceModal = ({ isOpen, onClose, service }) => {
//   if (!isOpen || !service) return null;

//   const discount = service.discount;
//   const hasDiscount = discount?.type && discount?.value;

//   const applyDiscount = (price) => {
//     if (!hasDiscount) return price;

//     if (discount.type === "percentage") {
//       return Math.round(price - (price * discount.value) / 100);
//     }

//     if (discount.type === "flat") {
//       return Math.max(price - discount.value, 0);
//     }

//     return price;
//   };

//   const discountLabel =
//     discount?.type === "percentage"
//       ? `${discount?.value}% OFF`
//       : `₹${discount?.value} OFF`;

//   const hasBasePrice = typeof service.basePrice === "number";
//   const hasTypePricing = service.type?.length > 0;

//   return (
//     <div
//       className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
//       onClick={onClose}
//     >
//       <div
//         className="bg-white rounded-xl max-h-[90vh] shadow-2xl w-full max-w-2xl overflow-y-scroll no-scrollbar"
//         onClick={(e) => e.stopPropagation()}
//       >
//         {/* Header / Image */}
//         <div className="relative">
//           {service.imageUrl ? (
//             <img
//               src={service.imageUrl}
//               alt={service.name}
//               className="w-full h-40 object-cover"
//             />
//           ) : (
//             <div className="w-full h-20 bg-gray-200 flex items-center justify-center">
//               <Tag size={48} className="text-gray-400" />
//             </div>
//           )}

//           {/* Overlay Title */}
//           <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4">
//             <h2 className="text-xl font-bold text-white">{service.name}</h2>
//             <p className="text-xs text-gray-200">
//               {service.categoryId?.categoryName} •{" "}
//               {service.subCategoryId?.subCategoryName}
//             </p>
//           </div>
//           <div className="absolute top-3 left-3 flex gap-2">
//             {hasDiscount && (
//               <span className="bg-green-600 text-white text-xs font-semibold px-2 py-1 rounded">
//                 {discountLabel}
//               </span>
//             )}

//             <span className="bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
//               ⭐ {service.ratings || 0}
//             </span>
//           </div>

//           {/* Close Button */}
//           <button
//             onClick={onClose}
//             className="absolute top-3 right-3 bg-white/80 rounded-full p-1.5 hover:bg-white transition"
//           >
//             <X size={18} />
//           </button>
//         </div>

//         {/* Body */}
//         <div className="p-5 space-y-6">
//           {/* Info Grid */}
//           <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
//             <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
//               <Clock size={20} className="text-[#EDB5A4]" />
//               <div>
//                 <p className="text-xs text-gray-500">Duration</p>
//                 <p className="text-sm font-semibold">{service.duration}</p>
//               </div>
//             </div>

//             <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
//               <Tag size={20} className="text-[#EDB5A4]" />
//               <div>
//                 <p className="text-xs text-gray-500">Category</p>
//                 <p className="text-sm font-semibold capitalize">
//                   {service.categoryId?.categoryName}
//                 </p>
//               </div>
//             </div>

//             <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
//               <Tag size={20} className="text-[#EDB5A4]" />
//               <div>
//                 <p className="text-xs text-gray-500">Sub Category</p>
//                 <p className="text-sm font-semibold capitalize">
//                   {service.subCategoryId?.subCategoryName}
//                 </p>
//               </div>
//             </div>
//           </div>

//           {/* Brand Pricing */}
//           {/* Pricing */}
//           {hasBasePrice && (
//             <div>
//               <h3 className="text-sm font-semibold text-gray-800 mb-3">
//                 Service Price
//               </h3>

//               <div className="flex justify-between items-center bg-gray-50 rounded-lg px-4 py-3 text-sm">
//                 <span className="text-gray-600">Base Price</span>
//                 <span className="flex items-center gap-1 font-semibold text-gray-900">
//                   <IndianRupee size={14} />
//                   {service.basePrice}
//                 </span>
//               </div>
//             </div>
//           )}

//           {!hasBasePrice && hasTypePricing && (
//             <div>
//               <h3 className="text-sm font-semibold text-gray-800 mb-3">
//                 Brand Pricing
//               </h3>

//               <div className="space-y-2">
//                 {service.type.map((item, idx) => {
//                   const finalPrice = applyDiscount(item.price);

//                   return (
//                     <div
//                       key={item._id || idx}
//                       className="flex justify-between bg-gray-50 rounded-lg px-4 py-2 text-sm"
//                     >
//                       <span>{item.brand}</span>

//                       <div className="flex items-center gap-2">
//                         {hasDiscount && (
//                           <span className="text-gray-400 line-through text-xs">
//                             ₹{item.price}
//                           </span>
//                         )}

//                         <span className="flex items-center gap-1 font-semibold text-gray-900">
//                           <IndianRupee size={14} />
//                           {finalPrice}
//                         </span>
//                       </div>
//                     </div>
//                   );
//                 })}
//               </div>
//             </div>
//           )}

//           {/* Extra Charges */}
//           {service.extraCharges?.length > 0 && (
//             <div>
//               <h3 className="text-sm font-semibold text-gray-800 mb-3">
//                 Extra Charges
//               </h3>
//               <div className="space-y-2">
//                 {service.extraCharges.map((item) => (
//                   <div
//                     key={item._id}
//                     className="flex justify-between bg-gray-50 rounded-lg px-4 py-2 text-sm"
//                   >
//                     <span>{item.title}</span>
//                     <span className="flex items-center gap-1 font-semibold">
//                       <IndianRupee size={14} />
//                       {item.amount}
//                     </span>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           )}
//           {/* DESCRIPTION */}
//           {service.description?.length > 0 && (
//             <div>
//               <h3 className="text-sm font-semibold text-gray-800 mb-3">
//                 Service Details
//               </h3>

//               <div className="space-y-4">
//                 {service.description.map((section) => (
//                   <div key={section._id}>
//                     <h4 className="text-sm font-semibold text-gray-900 mb-1">
//                       {section.heading}
//                     </h4>

//                     <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
//                       {section.points.map((point, idx) => (
//                         <li key={idx}>{point}</li>
//                       ))}
//                     </ul>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           )}

//           {/* Demo Video */}
//           {service.videoUrl ? (
//             <div>
//               <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-3">
//                 <PlayCircle size={18} className="text-[#EDB5A4]" />
//                 Demo Video
//               </h3>
//               <div className="rounded-lg overflow-hidden shadow">
//                 <video
//                   src={service.videoUrl}
//                   controls
//                   className="w-full h-[18rem] object-cover"
//                 />
//               </div>
//             </div>
//           ) : (
//             <div className="text-center py-6 text-gray-400 text-sm">
//               <PlayCircle size={36} className="mx-auto mb-2" />
//               No demo video available
//             </div>
//           )}
//         </div>

//         {/* Footer */}
//         <div className="p-4 bg-gray-50 border-t">
//           <button
//             onClick={onClose}
//             className="w-full px-6 py-3 bg-primaryBtnColor text-white font-semibold rounded-xl hover:bg-primaryBtnHoverColor transition"
//           >
//             Close
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ViewServiceModal;
